/**
 * PlanShowcase — Premium renderer for plan/agenda/viagem layout.
 *
 * Identity: organized, actionable, productivity tool.
 * Palette: deep navy + emerald green accent + amber next-action.
 * Desktop: Hero → NextAction → 2-col(Roadmap+Progress) → PhaseCards → Support → Chips
 * Mobile: stacked, fases expansíveis, próxima ação em destaque
 */

import React, { useState } from 'react';
import { ProseText } from './ProseText';
import { usePresentationBreakpoint, AthenaTip, ActionChips, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';

// ── Palette ──────────────────────────────────────────────────────────────────
const P = {
  card:       '#0c1220',
  border:     '#1a2240',
  borderS:    'rgba(30,34,64,0.5)',
  green:      '#34d399',
  greenSoft:  'rgba(52,211,153,0.10)',
  greenBdr:   'rgba(52,211,153,0.22)',
  greenL:     '#6ee7b7',
  greenD:     '#10b981',
  amber:      '#f59e0b',
  amberSoft:  'rgba(245,158,11,0.09)',
  amberBdr:   'rgba(245,158,11,0.25)',
  amberL:     '#fbbf24',
  blue:       '#60a5fa',
  blueSoft:   'rgba(96,165,250,0.09)',
  blueBdr:    'rgba(96,165,250,0.22)',
  red:        '#f87171',
  redSoft:    'rgba(248,113,113,0.09)',
  redBdr:     'rgba(248,113,113,0.22)',
  purple:     '#a78bfa',
  purpleSoft: 'rgba(167,139,250,0.09)',
  text:       '#e8eaf2',
  textSec:    'rgba(220,230,255,0.78)',
  textMut:    'rgba(150,170,220,0.55)',
  heroGrad:   'linear-gradient(135deg, #081810 0%, #0b1a14 100%)',
  nextGrad:   'linear-gradient(135deg, rgba(52,211,153,0.12) 0%, rgba(52,211,153,0.05) 100%)',
  tipGrad:    'linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0.04) 100%)',
};

// Status palette
const STATUS = {
  done:        { color: P.green,  soft: P.greenSoft,  bdr: P.greenBdr,  label: 'Concluída',    icon: '✓' },
  in_progress: { color: P.blue,   soft: P.blueSoft,   bdr: P.blueBdr,   label: 'Em andamento', icon: '⏳' },
  blocked:     { color: P.red,    soft: P.redSoft,    bdr: P.redBdr,    label: 'Bloqueada',    icon: '🚫' },
  delayed:     { color: P.amber,  soft: P.amberSoft,  bdr: P.amberBdr,  label: 'Atrasada',     icon: '⚠' },
  not_started: { color: P.textMut, soft: 'rgba(150,170,220,0.07)', bdr: 'rgba(150,170,220,0.18)', label: 'A iniciar', icon: '○' },
};

const PRIORITY = {
  critical: { color: P.red,    label: 'Crítica'  },
  high:     { color: P.amber,  label: 'Alta'     },
  medium:   { color: P.blue,   label: 'Média'    },
  low:      { color: P.textMut, label: 'Baixa'   },
};

// ── Metric chip ───────────────────────────────────────────────────────────────
function MetricChip({ icon, value, label }) {
  if (!value) return null;
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 2, padding: '6px 4px',
      background: 'rgba(255,255,255,0.024)', border: `1px solid ${P.border}`,
      borderRadius: 8,
    }}>
      <span style={{ fontSize: 12 }}>{icon}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color: P.text, textAlign: 'center', lineHeight: 1.2 }}>{value}</span>
      <span style={{ fontSize: 8, color: P.textMut, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
  );
}

// ── Progress ring ─────────────────────────────────────────────────────────────
function ProgressRing({ progress = 0, size = 88 }) {
  const r = size / 2 - 7;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(100, Math.max(0, progress)) / 100);
  return (
    <div style={{
      position: 'relative', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center',
      width: size, height: size,
    }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r}
          fill="none" stroke={P.greenSoft} strokeWidth={7}
          style={{ stroke: 'rgba(52,211,153,0.12)' }}
        />
        <circle cx={size/2} cy={size/2} r={r}
          fill="none" stroke={P.green} strokeWidth={7}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div style={{
        position: 'absolute', textAlign: 'center',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        <span style={{ fontSize: size > 80 ? 17 : 13, fontWeight: 800, color: P.green, lineHeight: 1 }}>
          {progress}%
        </span>
        <span style={{ fontSize: 7, color: P.textMut, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>
          progresso
        </span>
      </div>
    </div>
  );
}

// ── Hero (desktop) ────────────────────────────────────────────────────────────
function PlanHeroDesktop({ d }) {
  const depthMap   = { simples: 'Direto', médio: 'Estruturado', complexo: 'Completo' };
  const depthLabel = depthMap[d.depth_level] || d.depth_level;
  const phaseCount = d.section_count || (d.phases || []).length || null;
  const titleFs    = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const METRICS    = [
    d.duration    && { icon: '⏱', value: d.duration,                 label: 'Duração'      },
    phaseCount    && { icon: '🗺', value: `${phaseCount} fases`,      label: 'Etapas'       },
    depthLabel    && { icon: '📊', value: depthLabel,                 label: 'Complexidade' },
    d.progress != null && { icon: '🎯', value: `${d.progress}%`,     label: 'Progresso'    },
  ].filter(Boolean);

  return (
    <div style={{ background: 'linear-gradient(135deg, #081810 0%, #0b1a14 100%)', border: `1px solid ${P.border}`,
      borderRadius: 12, overflow: 'hidden', display: 'flex', height: 260,
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge="📋 PLANO / OBJETIVOS"
        accentColor={P.green} accentFade={P.greenSoft} accentBdr={P.greenBdr}
        placeholder="📋" placeholderLabel="Plano"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          {(d.priority || d.status) && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
              {d.priority && PRIORITY[d.priority] && (
                <div style={{ padding: '1px 8px', borderRadius: 20, background: `${PRIORITY[d.priority].color}18`, border: `1px solid ${PRIORITY[d.priority].color}40`, fontSize: 9, fontWeight: 700, color: PRIORITY[d.priority].color }}>
                  {PRIORITY[d.priority].label}
                </div>
              )}
              {d.status && STATUS[d.status] && (
                <div style={{ padding: '1px 8px', borderRadius: 20, background: STATUS[d.status].soft, border: `1px solid ${STATUS[d.status].bdr}`, fontSize: 9, fontWeight: 700, color: STATUS[d.status].color }}>
                  {STATUS[d.status].icon} {STATUS[d.status].label}
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize: titleFs, fontWeight: 800, color: P.text, lineHeight: 1.2, letterSpacing: '-0.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</div>
          {d.subtitle && d.subtitle !== d.title && (
            <div style={{ fontSize: 12, color: P.textSec, lineHeight: 1.45, marginTop: 6,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{d.subtitle}</div>
          )}
        </div>
        {METRICS.length > 0 && <MetricStrip metrics={METRICS} accentColor={P.green} />}
      </div>
    </div>
  );
}

// ── Hero (mobile) ─────────────────────────────────────────────────────────────
function PlanHeroMobile({ d }) {
  const depthMap   = { simples: 'Direto', médio: 'Estruturado', complexo: 'Completo' };
  const depthLabel = depthMap[d.depth_level] || d.depth_level || 'Plano';
  const phaseCount = d.section_count || (d.phases || []).length;
  const METRICS    = [
    { icon: '⏱', value: d.duration  || 'Planejado',                     label: 'Duração'   },
    { icon: '🗺', value: phaseCount > 0 ? `${phaseCount}` : 'Etapas',   label: 'Fases'     },
    { icon: '📊', value: depthLabel,                                     label: 'Plano'     },
    { icon: '🎯', value: d.progress != null ? `${d.progress}%` : 'Em andamento', label: 'Progresso' },
  ];

  return (
    <div style={{ background: 'linear-gradient(135deg, #081810 0%, #0b1a14 100%)', border: `1px solid ${P.border}`,
      borderRadius: 10, overflow: 'hidden',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, alignSelf: 'flex-start',
            padding: '2px 9px', borderRadius: 20, marginBottom: 10,
            background: P.greenSoft, border: `1px solid ${P.greenBdr}`,
            fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
            color: P.greenL, textTransform: 'uppercase',
          }}>📋 PLANO / OBJETIVOS</div>
          <div style={{ fontSize: 19, fontWeight: 800, color: P.text, lineHeight: 1.2 }}>{d.title}</div>
          {d.subtitle && d.subtitle !== d.title && (
            <div style={{ fontSize: 12, color: P.textSec, lineHeight: 1.4, marginTop: 5,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{d.subtitle}</div>
          )}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={P.green} accentFade={P.greenSoft} accentBdr={P.greenBdr}
          placeholder="📋"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${P.border}`, background: 'rgba(0,0,0,0.25)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={P.green} />
      </div>
    </div>
  );
}

// ── Next action card ──────────────────────────────────────────────────────────
function NextActionCard({ action }) {
  if (!action) return null;
  return (
    <div style={{
      background: P.nextGrad,
      border: `1px solid ${P.greenBdr}`,
      borderLeft: `3px solid ${P.green}`,
      borderRadius: 10, padding: '12px 14px',
      display: 'flex', alignItems: 'flex-start', gap: 12,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: '50%',
        background: P.greenSoft, border: `1px solid ${P.greenBdr}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, flexShrink: 0,
      }}>
        ▶
      </div>
      <div>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
          textTransform: 'uppercase', color: `${P.green}bb`, marginBottom: 4,
        }}>
          PRÓXIMA AÇÃO
        </div>
        <div style={{
          fontSize: 14, fontWeight: 600, color: P.text, lineHeight: 1.45,
        }}>
          {action}
        </div>
      </div>
    </div>
  );
}

// ── Roadmap map ───────────────────────────────────────────────────────────────
function RoadmapMap({ phases }) {
  if (!phases || phases.length === 0) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, padding: '10px 12px', height: '100%',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut, marginBottom: 9,
      }}>
        🗺 ROADMAP
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {phases.map((phase, i) => {
          const pTitle = phase.title || phase.label || `Fase ${i + 1}`;
          const pStatus = phase.status || 'not_started';
          const st = STATUS[pStatus] || STATUS.not_started;
          const taskCount = (phase.tasks || phase.items || []).length;
          return (
            <div key={phase.id || i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {/* Number / status dot */}
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: pStatus === 'done' ? P.green : P.greenSoft,
                border: `1px solid ${P.greenBdr}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: pStatus === 'done' ? 11 : 9, fontWeight: 800,
                color: pStatus === 'done' ? '#fff' : P.greenL,
                flexShrink: 0,
              }}>
                {pStatus === 'done' ? '✓' : i + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: P.textSec,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {pTitle}
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 1 }}>
                  <span style={{
                    fontSize: 9, color: st.color,
                    fontWeight: 600,
                  }}>
                    {st.icon} {st.label}
                  </span>
                  {taskCount > 0 && (
                    <span style={{ fontSize: 9, color: P.textMut }}>
                      · {taskCount} tarefa{taskCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              {/* Connector line */}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Progress panel (sidebar-style on desktop) ─────────────────────────────────
function ProgressPanel({ progress, phases }) {
  if (progress == null && (!phases || phases.length === 0)) return null;
  const done = (phases || []).filter(p => p.status === 'done').length;
  const total = (phases || []).length;
  const pct = progress != null ? progress : (total > 0 ? Math.round((done / total) * 100) : 0);

  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut, alignSelf: 'flex-start',
      }}>
        PROGRESSO GERAL
      </div>
      <ProgressRing progress={pct} size={88} />
      {total > 0 && (
        <div style={{
          fontSize: 11, color: P.textSec, textAlign: 'center',
        }}>
          {done} de {total} fases concluídas
        </div>
      )}
    </div>
  );
}

// ── Phase card ────────────────────────────────────────────────────────────────
function PhaseCard({ phase, index, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen !== false);
  const pTitle = phase.title || phase.label || `Fase ${index + 1}`;
  const tasks  = phase.tasks || phase.items || [];
  const pStatus = phase.status || 'not_started';
  const st = STATUS[pStatus] || STATUS.not_started;
  const prio = phase.priority ? PRIORITY[phase.priority] : null;

  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderLeft: `3px solid ${st.color}`,
      borderRadius: 10, overflow: 'hidden',
    }}>
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        {/* Phase number */}
        <div style={{
          width: 22, height: 22, borderRadius: '50%',
          background: pStatus === 'done' ? P.green : P.greenSoft,
          border: `1px solid ${P.greenBdr}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: pStatus === 'done' ? 11 : 9, fontWeight: 800,
          color: pStatus === 'done' ? '#fff' : P.greenL,
          flexShrink: 0,
        }}>
          {pStatus === 'done' ? '✓' : index + 1}
        </div>

        {/* Title */}
        <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: P.text, lineHeight: 1.3 }}>
          {pTitle}
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexShrink: 0 }}>
          {prio && (
            <div style={{
              padding: '1px 7px', borderRadius: 20,
              background: `${prio.color}15`, border: `1px solid ${prio.color}38`,
              fontSize: 9, fontWeight: 700, color: prio.color,
            }}>
              {prio.label}
            </div>
          )}
          <div style={{
            padding: '1px 7px', borderRadius: 20,
            background: st.soft, border: `1px solid ${st.bdr}`,
            fontSize: 9, fontWeight: 700, color: st.color,
          }}>
            {st.icon} {st.label}
          </div>
          <span style={{ color: P.textMut, fontSize: 11, marginLeft: 2 }}>
            {open ? '▾' : '▸'}
          </span>
        </div>
      </button>

      {/* Tasks */}
      {open && tasks.length > 0 && (
        <div style={{
          padding: '2px 14px 12px 46px',
          borderTop: `1px solid ${P.borderS}`,
        }}>
          {tasks.map((task, j) => (
            <div key={j} style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '5px 0',
              borderBottom: j < tasks.length - 1 ? `1px solid ${P.borderS}` : 'none',
              fontSize: 12.5, color: P.textSec, lineHeight: 1.45,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 2,
                background: P.greenSoft, border: `1px solid ${P.greenBdr}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 8, color: P.green,
              }}>
                {pStatus === 'done' ? '✓' : ''}
              </div>
              <span>{task}</span>
            </div>
          ))}
        </div>
      )}
      {open && tasks.length === 0 && phase.objective && (
        <div style={{
          padding: '4px 14px 12px 46px',
          borderTop: `1px solid ${P.borderS}`,
          fontSize: 12.5, color: P.textSec, lineHeight: 1.55,
        }}>
          {phase.objective}
        </div>
      )}
    </div>
  );
}

// ── Milestone track ───────────────────────────────────────────────────────────
function MilestoneTrack({ milestones }) {
  if (!milestones || milestones.length === 0) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, padding: '10px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut, marginBottom: 8,
      }}>
        🏁 MARCOS
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {milestones.map((m, i) => (
          <div key={i} style={{
            padding: '4px 10px', borderRadius: 20,
            background: P.amberSoft, border: `1px solid ${P.amberBdr}`,
            fontSize: 11, fontWeight: 600, color: P.amberL,
          }}>
            🏁 {m.label || m}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Resource block ────────────────────────────────────────────────────────────
function ResourceBlock({ resources }) {
  if (!resources) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderLeft: `3px solid ${P.blue}`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: 'rgba(96,165,250,0.7)',
        marginBottom: 8,
      }}>
        🔧 RECURSOS NECESSÁRIOS
      </div>
      <ProseText
        text={resources}
        style={{ fontSize: 13, color: P.textSec, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}
      />
    </div>
  );
}

// ── Risk block ────────────────────────────────────────────────────────────────
function RiskBlock({ risks }) {
  if (!risks) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderLeft: `3px solid rgba(248,113,113,0.6)`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: 'rgba(248,113,113,0.7)',
        marginBottom: 8,
      }}>
        ⚠ RISCOS E BLOQUEIOS
      </div>
      <ProseText
        text={risks}
        style={{ fontSize: 13, color: P.textSec, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}
      />
    </div>
  );
}

// ── Success metrics ───────────────────────────────────────────────────────────
function SuccessBlock({ metrics }) {
  if (!metrics) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderLeft: `3px solid ${P.green}`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: `${P.green}bb`,
        marginBottom: 8,
      }}>
        📈 INDICADORES DE SUCESSO
      </div>
      <ProseText
        text={metrics}
        style={{ fontSize: 13, color: P.textSec, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}
      />
    </div>
  );
}

// ── Summary card (sidebar) ────────────────────────────────────────────────────
function SummaryCard({ d }) {
  const depthMap = { simples: 'Simples', médio: 'Estruturado', complexo: 'Completo' };
  const rows = [
    d.duration    && { icon: '⏱', label: 'Duração',       value: d.duration },
    d.priority    && PRIORITY[d.priority] && { icon: '⚡', label: 'Prioridade', value: PRIORITY[d.priority].label },
    d.status      && STATUS[d.status]    && { icon: '📌', label: 'Status',     value: STATUS[d.status].label },
    d.depth_level && { icon: '📊', label: 'Plano',         value: depthMap[d.depth_level] || d.depth_level },
  ].filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, overflow: 'hidden',
    }}>
      <div style={{
        padding: '7px 12px', borderBottom: `1px solid ${P.border}`,
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut,
      }}>
        RESUMO
      </div>
      {rows.map((it, i) => (
        <div key={i} style={{
          display: 'flex', gap: 8, padding: '7px 12px',
          borderBottom: i < rows.length - 1 ? `1px solid rgba(30,34,64,0.5)` : 'none',
          fontSize: 12, alignItems: 'flex-start',
        }}>
          <span>{it.icon}</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 9, color: P.textMut, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {it.label}
            </span>
            <span style={{ color: P.textSec, fontWeight: 500 }}>{it.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Actions card (sidebar) ────────────────────────────────────────────────────
function ActionsCard({ onAction }) {
  const acts = [
    { id: 'Detalhar fase 1',  icon: '📋', label: 'Detalhar fase 1'  },
    { id: 'Ajustar prazo',    icon: '📅', label: 'Ajustar prazo'    },
    { id: 'Adicionar tarefa', icon: '➕', label: 'Adicionar tarefa' },
    { id: 'Exportar',         icon: '📤', label: 'Exportar plano'   },
  ];
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, overflow: 'hidden',
    }}>
      <div style={{
        padding: '7px 12px', borderBottom: `1px solid ${P.border}`,
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut,
      }}>
        AÇÕES
      </div>
      {acts.map((a, i) => (
        <button
          key={i}
          onClick={() => onAction && onAction(`follow:${a.id}`)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', background: 'transparent', border: 'none',
            borderBottom: i < acts.length - 1 ? `1px solid rgba(30,34,64,0.5)` : 'none',
            color: P.textSec, fontSize: 12, cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span>{a.icon}</span>
          <span>{a.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function PlanShowcase({ data, wp, onAction }) {
  const d = wp || data;
  const isMobile = usePresentationBreakpoint(640);
  if (!d) return null;

  const phases   = d.phases || [];
  const hasPhases = phases.length > 0;
  const hasProgress = d.progress != null || phases.length > 0;
  const chips = d.follow_up_chips || ["Detalhar fase 1", "Ajustar prazo", "Adicionar tarefa",
                                       "Simplificar plano", "Ver riscos", "Criar lembrete"];
  const showSidebar = !isMobile && (d.duration || d.priority || d.status || d.depth_level);

  // ── Mobile ──────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <PlanHeroMobile d={d} />
        {hasProgress && (
          <div style={{
            background: P.card, border: `1px solid ${P.border}`,
            borderRadius: 10, padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <ProgressRing progress={d.progress ?? Math.round((phases.filter(p=>p.status==='done').length/Math.max(phases.length,1))*100)} size={72} />
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: P.textMut, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>VISÃO GERAL</div>
              {d.duration && <div style={{ fontSize: 12, color: P.textSec }}>⏱ {d.duration}</div>}
              {phases.length > 0 && <div style={{ fontSize: 12, color: P.textSec }}>🗺 {phases.length} fases</div>}
            </div>
          </div>
        )}
        <NextActionCard action={d.next_action} />
        {hasPhases && phases.map((phase, i) => (
          <PhaseCard key={phase.id || i} phase={phase} index={i} defaultOpen={i === 0} />
        ))}
        <MilestoneTrack milestones={d.milestones} />
        <ResourceBlock resources={d.resources} />
        <RiskBlock risks={d.risks} />
        <SuccessBlock metrics={d.success_metrics} />
        <AthenaTip tip={d.tip} />
        <ActionChips chips={chips} onAction={onAction} accentColor={P.green} />
      </div>
    );
  }

  // ── Desktop ──────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%' }}>

      {/* Main column */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <PlanHeroDesktop d={d} />

        {/* Next action — most prominent */}
        <NextActionCard action={d.next_action} />

        {/* Two-column: Roadmap + Progress */}
        {hasPhases && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
            <div style={{ flex: '0 0 55%' }}>
              <RoadmapMap phases={phases} />
            </div>
            {hasProgress && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <ProgressPanel progress={d.progress} phases={phases} />
              </div>
            )}
          </div>
        )}

        {/* Expandable phase cards */}
        {hasPhases && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {phases.map((phase, i) => (
              <PhaseCard
                key={phase.id || i}
                phase={phase}
                index={i}
                defaultOpen={phases.length <= 4}
              />
            ))}
          </div>
        )}

        <MilestoneTrack milestones={d.milestones} />
        <ResourceBlock resources={d.resources} />
        <RiskBlock risks={d.risks} />
        <SuccessBlock metrics={d.success_metrics} />
        <AthenaTip tip={d.tip} />
        <ActionChips chips={chips} onAction={onAction} accentColor={P.green} />
      </div>

      {/* Optional sidebar */}
      {showSidebar && (
        <div style={{
          width: 200, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <SummaryCard d={d} />
          <ActionsCard onAction={onAction} />
        </div>
      )}
    </div>
  );
}
